import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSessionAdvisor } from '@client/model/selectors';
import {
  deleteAdvisorBgaCarrierSettingsByAdvisorIdRequest,
  postAdvisorBgaCarrierSettingsByAdvisorIdRequest,
} from '@client/model/advisorBgaCarrierSettingsModel';
import { newUuid } from '@shared';
import { useBgaData } from './useBgaData';

const bgaCarrierMetaSchema = z.object({
  writingCode: z.string().optional(),
});

const bgaEntrySchema = z.object({
  advisorOrganizationId: z.string(),
  advisorId: z.string(),
  name: z
    .string()
    .min(1, 'Company name is required')
    .max(100, 'Company name must be 100 characters or less')
    .regex(/^[a-zA-Z0-9\s\-.,&'()]+$/, 'Company name contains invalid characters'),
  contactEmail: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(254, 'Email must be 254 characters or less')
    .regex(
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
      'Email format is invalid',
    ),
  assignedCarrierId: z.number().optional(),
  carrierMeta: bgaCarrierMetaSchema.default({}),
});

const bgaFormSchema = z.object({
  bgas: z.array(bgaEntrySchema),
});

type BgaFormData = z.infer<typeof bgaFormSchema>;
type BgaFormInput = z.input<typeof bgaFormSchema>;

export function useBgaManagement() {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);

  // Use the new data fetching hook
  const { bgas: bgaData, isLoading, hasError, status } = useBgaData();

  // Initialize form with Redux data
  const form = useForm<BgaFormInput, unknown, BgaFormData>({
    resolver: zodResolver(bgaFormSchema),
    defaultValues: {
      bgas: [],
    },
  });

  const { remove, update } = useFieldArray({
    control: form.control,
    name: 'bgas',
  });

  // Sync Redux data to form when data changes
  useEffect(() => {
    // Always sync Redux data to form, regardless of length
    // This ensures the form is properly initialized with an empty array when no data exists
    form.reset({ bgas: bgaData || [] });
  }, [bgaData, form]);

  // Every entry is written with carrierMeta (API data, addBga, updates), so the raw form state
  // already has the parsed BgaEntry shape; the schema's default only guards validation.
  const formBgas = (form.watch('bgas') || []) as BgaEntry[];

  const findBgaIndex = (bgaOrganizationId: string) => {
    return formBgas.findIndex(bga => bga.advisorOrganizationId === bgaOrganizationId);
  };

  const addBga = () => {
    const newBgaId = newUuid();
    const newBga: BgaEntry = {
      advisorOrganizationId: newBgaId,
      advisorId: advisor.advisorId,
      name: '',
      contactEmail: '',
      assignedCarrierId: undefined,
      carrierMeta: {},
    };
    // Prepend to the beginning of the array so it appears at the top
    const currentBgas = form.getValues('bgas');
    form.setValue('bgas', [newBga, ...currentBgas]);
    setEditingBgaId(newBgaId); // Set edit mode for the new BGA
  };

  const removeBga = (bgaOrganizationId: string) => {
    const index = findBgaIndex(bgaOrganizationId);
    if (index !== -1) {
      dispatch(
        deleteAdvisorBgaCarrierSettingsByAdvisorIdRequest({
          advisorId: formBgas[index].advisorId,
          advisorOrganizationId: bgaOrganizationId,
        }),
      );
      remove(index);
    }
  };

  const updateBga = (bgaOrganizationId: string, updates: Partial<BgaEntry>) => {
    const index = findBgaIndex(bgaOrganizationId);
    if (index !== -1) {
      const currentBga = formBgas[index];
      update(index, { ...currentBga, ...updates });
    }
  };

  const assignCarrierToBga = (bgaOrganizationId: string, carrierId: number) => {
    const index = findBgaIndex(bgaOrganizationId);
    if (index !== -1) {
      const currentBga = formBgas[index];
      update(index, { ...currentBga, assignedCarrierId: carrierId });
    }
  };

  const removeCarrierFromBga = (bgaOrganizationId: string) => {
    const index = findBgaIndex(bgaOrganizationId);
    if (index !== -1) {
      const bga = formBgas[index];
      if (bga.assignedCarrierId) {
        update(index, {
          ...bga,
          assignedCarrierId: undefined,
          carrierMeta: {},
        });
      }
    }
  };

  const updateCarrierMeta = (
    bgaOrganizationId: string,
    carrierId: number,
    meta: Partial<BgaCarrierMeta>,
  ) => {
    const index = findBgaIndex(bgaOrganizationId);
    if (index !== -1) {
      const bga = formBgas[index];
      const updatedCarrierMeta = {
        ...bga.carrierMeta,
        ...meta,
      };
      update(index, { ...bga, carrierMeta: updatedCarrierMeta });
    }
  };

  const isCarrierAssigned = (carrierId: number) => {
    return formBgas.some(bga => bga.assignedCarrierId === carrierId);
  };

  const getBgaForCarrier = (carrierId: number) => {
    return formBgas.find(bga => bga.assignedCarrierId === carrierId);
  };

  const [editingBgaId, setEditingBgaId] = useState<string | null>(null);

  const saveBga = async () => {
    // Validate the entire form before saving
    const isValid = await form.trigger();

    if (isValid) {
      dispatch(
        postAdvisorBgaCarrierSettingsByAdvisorIdRequest({
          advisorId: advisor.advisorId,
          bgas: formBgas,
        }),
      );
      setEditingBgaId(null);
    }
  };

  const cancelBgaEdit = (bgaOrganizationId: string) => {
    const index = findBgaIndex(bgaOrganizationId);
    if (index !== -1) {
      // Find the original BGA data from Redux store
      const originalBga = bgaData?.find(b => b.advisorOrganizationId === bgaOrganizationId);

      if (originalBga) {
        // Revert to original data
        update(index, originalBga);
      } else {
        // This is a new BGA that hasn't been saved yet, remove it
        remove(index);
      }
    }
    setEditingBgaId(null);
  };

  const getFieldError = (bgaOrganizationId: string, fieldName: 'name' | 'contactEmail') => {
    const index = findBgaIndex(bgaOrganizationId);
    if (index === -1) return undefined;

    const fieldError = form.formState.errors.bgas?.[index]?.[fieldName];
    return fieldError?.message;
  };

  const validateField = async (bgaOrganizationId: string, fieldName: 'name' | 'contactEmail') => {
    const index = findBgaIndex(bgaOrganizationId);
    if (index !== -1) {
      await form.trigger(`bgas.${index}.${fieldName}`);
    }
  };

  return {
    form,
    bgas: formBgas,
    bgaSettings: { data: bgaData, status, hasError },
    isLoading,
    editingBgaId,
    setEditingBgaId,
    addBga,
    removeBga,
    updateBga,
    assignCarrierToBga,
    removeCarrierFromBga,
    updateCarrierMeta,
    saveBga,
    cancelBgaEdit,
    isCarrierAssigned,
    getBgaForCarrier,
    getFieldError,
    validateField,
  };
}
